import { useState, useCallback } from "react";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  ADD_MENU_ITEM_OPTION,
  ADD_ITEM_OPTION_ELEMENT,
  UPDATE_ITEM_OPTION,
  UPDATE_ITEM_OPTION_ELEMENT,
} from "src/constants/mutations";
import { successToast } from "src/utils/toasts";
import { AddItemOptionElementInput, AddItemOptionInput, MenuItemOptionElement, UpdateItemOptionInput } from "src/interfaces/menu.interface";
import { AddMenuItemOptionSuccessPayload, MutationCallbacks } from "src/interfaces/menu-items-props.interface";
import { UUID } from "src/types/shared.type";

export const useItemOptionMutations = () => {
  const { axBe } = useAxiosInterceptor();

  const [isUpdatingOption, setIsUpdatingOption] = useState(false);
  const [isAddingMenuItemOption, setIsAddingMenuItemOption] = useState(false);
  const [isUpdatingMenuItemOptionElement, setIsUpdatingMenuItemOptionElement] = useState(false);
  const [isAddingMenuItemOptionElement, setIsAddingMenuItemOptionElement] = useState(false);

  const updateMenuItemOptionMutation = useCallback(async (
    updateMenuItemOption: UpdateItemOptionInput,
    callbacks?: MutationCallbacks
  ) => {
    setIsUpdatingOption(true);
    try {
      const variables = {
        updateItemOptionInput: {
          ...updateMenuItemOption,
          nonEnglishName: updateMenuItemOption.nonEnglishName || undefined,
          translations: updateMenuItemOption.translations,
        },
      };
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_ITEM_OPTION,
        variables,
      });
      if (data?.errors?.length) throw data.errors[0];
      successToast("Item Option Updated!");
      callbacks?.onSuccess?.();
    } catch (error) {
      callbacks?.onError?.(error);
    } finally {
      setIsUpdatingOption(false);
    }
  }, [axBe]);

  const addMenuItemOptionMutation = useCallback(async (
    menuItemOption: AddItemOptionInput,
    menuItemCorrelationId: UUID,
    callbacks?: MutationCallbacks<AddMenuItemOptionSuccessPayload>
  ) => {
    setIsAddingMenuItemOption(true);
    try {
      const { name, description, renderSequence, isRequired, multipleSelectionsAllowed } = menuItemOption;
  
      const filteredMenuItemOptionElements = menuItemOption?.addItemOptionElementInputList
        ?.map((menuItemOptionElement: AddItemOptionElementInput) => ({
          name: menuItemOptionElement?.name?.trim(),
          nonEnglishName: menuItemOptionElement?.nonEnglishName?.trim() || undefined,
          description: menuItemOptionElement?.description?.trim(),
          priceCents: menuItemOptionElement?.priceCents,
          renderSequence: Number(menuItemOptionElement?.renderSequence),
          translations: menuItemOptionElement?.translations,
        }))
        ?.filter(
          (menuItemOptionElement) =>
            menuItemOptionElement.name && menuItemOptionElement.priceCents !== null && menuItemOptionElement.priceCents !== undefined
        );
  
      const addItemOptionInput: AddItemOptionInput= {
          itemCorrelationId: menuItemCorrelationId,
          name : name || "",
          nonEnglishName: menuItemOption.nonEnglishName || undefined,
          description : description || "",
          renderSequence: Number(renderSequence),
          isRequired : isRequired || false,
          multipleSelectionsAllowed : multipleSelectionsAllowed || false,
          translations: menuItemOption.translations,
      };
      // Only include addItemOptionElementInputList if valid entries exist
      if (filteredMenuItemOptionElements && filteredMenuItemOptionElements.length > 0) {
        addItemOptionInput.addItemOptionElementInputList = filteredMenuItemOptionElements;
      }
  
      const variables = { addItemOptionInput };

      const { data : addedMenuItemOption } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: ADD_MENU_ITEM_OPTION,
        variables,
      });
  
      if (addedMenuItemOption?.errors?.length) throw addedMenuItemOption.errors[0];
  
      const { correlationId, itemOptionElements: menuItemOptionElements } =
        addedMenuItemOption?.data?.addItemOption || {};
  
      successToast("Item Option Added!");
      callbacks?.onSuccess?.({ correlationId, menuItemOptionElements });
    } catch (error) {
      callbacks?.onError?.(error);
    } finally {
      setIsAddingMenuItemOption(false);
    }
  }, [axBe]);
  

  const updateMenuItemOptionElementMutation = useCallback(async (
    menuItemOptionElement: MenuItemOptionElement,
    isDeleted: boolean,
    callbacks?: MutationCallbacks
  ) => {
    setIsUpdatingMenuItemOptionElement(true);
    try {
      const { name, nonEnglishName, description, renderSequence, priceCents, correlationId, translations } = menuItemOptionElement;
      const variables = {
        updateItemOptionElementInput: {
          itemOptionElementCorrelationId: correlationId,
          name,
          nonEnglishName: nonEnglishName || undefined,
          description,
          renderSequence: Number(renderSequence),
          priceCents,
          isActive: isDeleted ? false : true,
          translations,
        },
      };
      const { data : updatedMenuItemOptionElement } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_ITEM_OPTION_ELEMENT,
        variables,
      });
      if (updatedMenuItemOptionElement?.errors?.length) throw updatedMenuItemOptionElement.errors[0];
      successToast(isDeleted ? "Option Element Deleted!" : "Option Element Updated!");
      callbacks?.onSuccess?.();
    } catch (error) {
      callbacks?.onError?.(error);
    } finally {
      setIsUpdatingMenuItemOptionElement(false);
    }
  }, [axBe]);

  const addMenuItemOptionElementMutation = useCallback(async (
    menuItemOptionCorrelationId: UUID,
    menuItemOptionElement: MenuItemOptionElement,
    callbacks?: MutationCallbacks<UUID>
  ) => {
    setIsAddingMenuItemOptionElement(true);
    try {
      const { name, nonEnglishName, description, renderSequence, priceCents, translations } = menuItemOptionElement;
      const variables = {
        addItemOptionElementInput: {
          itemOptionCorrelationId: menuItemOptionCorrelationId,
          name,
          nonEnglishName: nonEnglishName || undefined,
          description,
          renderSequence: Number(renderSequence),
          priceCents,
          translations,
        },
      };
      const { data : addedMenuItemOptionElement } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: ADD_ITEM_OPTION_ELEMENT,
        variables,
      });
      if (addedMenuItemOptionElement?.errors?.length) throw addedMenuItemOptionElement.errors[0];
      successToast("Option Element Added!");
      const menuItemOptionElementCorrelationId = addedMenuItemOptionElement?.data?.addItemOptionElement?.correlationId;
      callbacks?.onSuccess?.(menuItemOptionElementCorrelationId);
    } catch (error) {
      callbacks?.onError?.(error);
    } finally {
      setIsAddingMenuItemOptionElement(false);
    }
  }, [axBe]);

  return {
    // flags
    isUpdatingOption,
    isAddingMenuItemOption,
    isUpdatingMenuItemOptionElement,
    isAddingMenuItemOptionElement,
    // actions
    updateMenuItemOptionMutation,
    addMenuItemOptionMutation,
    updateMenuItemOptionElementMutation,
    addMenuItemOptionElementMutation,
  };
};
